/**
 * Branded page heading — small Forge Ledger shield in a rounded tile
 * to the left of the H1 + optional subtitle. Use at the top of any
 * customer-facing page so the brand sits inside every primary heading,
 * not just the navbar.
 *
 *   <PageTitle title="Accounts" subtitle="Manage your accounts" />
 *
 * Variants:
 *   - `icon`     — pass any lucide icon to override the shield logo
 *                  (e.g. an admin page might want ShieldCheck or
 *                  ToggleRight inside the tile instead).
 *   - `iconTint` — tile background colour. Defaults to a soft brand
 *                  blue. Pass e.g. `bg-[#FEF3C7]` to recolour for
 *                  admin / warning surfaces.
 *   - `right`    — slot for inline actions (e.g. "Add account" button)
 *                  that should sit on the same row as the heading.
 *
 * Designed to slot into existing pages with one line; we deliberately
 * keep the spacing (mb-6) reserved by the heading so we don't have to
 * touch each page's content block underneath.
 */
import React from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png';

const PageTitle = ({
  title,
  subtitle,
  icon: Icon,
  iconTint = 'bg-[#EFF6FF]',
  iconColor = 'text-[#0052FF]',
  right,
  size = 'lg',
  className = '',
  testId,
}) => {
  const titleSize = size === 'sm'
    ? 'text-2xl sm:text-3xl'
    : 'text-3xl sm:text-4xl';

  return (
    <div
      className={`flex items-start sm:items-center justify-between gap-4 mb-6 flex-wrap ${className}`}
      data-testid={testId || 'page-title'}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl ${iconTint} flex items-center justify-center flex-shrink-0 overflow-hidden`}
          aria-hidden="true"
        >
          {Icon ? (
            <Icon className={`w-6 h-6 ${iconColor}`} />
          ) : (
            <img
              src={LOGO_URL}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="min-w-0">
          <h1 className={`${titleSize} font-extrabold tracking-tight text-[#020617] leading-tight truncate`}>
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-[#64748B] mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {right && (
        <div className="flex items-center gap-2 flex-shrink-0">
          {right}
        </div>
      )}
    </div>
  );
};

export default PageTitle;
